import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { EventFilterOption } from '@bailafm/shared';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, MapPin, Plus, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { ApiClientError, citiesApi, featuredCitiesApi } from '@/lib/api';
import { cn } from '@/lib/utils';

// Gestor de ciudades destacadas de la home ("Elige tu ciudad"). Una
// sola lista ordenada (singleton). A diferencia de los artistas, una
// ciudad es sólo un nombre: el selector saca las ciudades disponibles
// de la faceta del store y filtra en cliente (son pocas). Reordenar con
// drag&drop, eliminar, guardar con If-Match.

export default function CiudadesDestacadasPage() {
  const [selected, setSelected] = useState<string[]>([]);
  const [etag, setEtag] = useState<string | undefined>(undefined);
  const [savedCities, setSavedCities] = useState<string[]>([]);
  const [available, setAvailable] = useState<EventFilterOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Carga inicial: el singleton (nombres ordenados) + la faceta de
  // ciudades del store para el selector.
  const refresh = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [item, cities] = await Promise.all([
        featuredCitiesApi.get(),
        citiesApi.list(),
      ]);
      const ordered = [...item.cities]
        .sort((a, b) => a.position - b.position)
        .map((c) => c.name);
      setSelected(ordered);
      setSavedCities(ordered);
      setEtag(item._etag);
      setAvailable(cities);
    } catch (err) {
      setLoadError(
        err instanceof ApiClientError
          ? err.message
          : 'No se pudieron cargar las ciudades destacadas.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Cierra el dropdown al pulsar fuera.
  useEffect(() => {
    if (!searchOpen) return;
    function handle(event: PointerEvent) {
      const root = searchContainerRef.current;
      if (root && !root.contains(event.target as Node)) setSearchOpen(false);
    }
    document.addEventListener('pointerdown', handle);
    return () => document.removeEventListener('pointerdown', handle);
  }, [searchOpen]);

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  // Resultados del selector: ciudades de la faceta que no estén ya
  // añadidas, filtradas por el término escrito (en cliente).
  const results = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return available
      .filter((c) => !selectedSet.has(c.displayName))
      .filter((c) => !needle || c.displayName.toLowerCase().includes(needle));
  }, [available, selectedSet, search]);

  const dirty = useMemo(() => {
    if (selected.length !== savedCities.length) return true;
    return selected.some((name, i) => name !== savedCities[i]);
  }, [selected, savedCities]);

  const handleAdd = useCallback((name: string) => {
    setSelected((prev) => (prev.includes(name) ? prev : [...prev, name]));
    setSearch('');
    setSearchOpen(false);
  }, []);

  const handleRemove = useCallback((name: string) => {
    setSelected((prev) => prev.filter((n) => n !== name));
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSelected((prev) => {
      const oldIndex = prev.indexOf(String(active.id));
      const newIndex = prev.indexOf(String(over.id));
      if (oldIndex < 0 || newIndex < 0) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  }, []);

  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await featuredCitiesApi.update(etag, { cities: selected });
      const ordered = [...updated.cities]
        .sort((a, b) => a.position - b.position)
        .map((c) => c.name);
      setSavedCities(ordered);
      setEtag(updated._etag);
      toast.success('Ciudades destacadas guardadas');
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? formatApiError(err)
          : 'No se pudieron guardar las ciudades destacadas.';
      setSaveError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-3xl font-semibold tracking-tight">
            Ciudades destacadas
          </h1>
          <p className="text-muted-foreground">
            Elige y ordena las ciudades que aparecen en la home. El orden se
            modifica arrastrando.
          </p>
        </div>
        <Button onClick={handleSave} disabled={!dirty || saving || loading}>
          {saving ? 'Guardando…' : 'Guardar'}
        </Button>
      </header>

      {loadError ? (
        <div className="space-y-3 rounded-md border border-destructive/40 bg-destructive/5 p-4">
          <p className="text-sm text-destructive">{loadError}</p>
          <Button variant="outline" size="sm" onClick={() => void refresh()}>
            Reintentar
          </Button>
        </div>
      ) : (
        <>
          <section className="space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Añadir ciudades
            </h3>
            <div ref={searchContainerRef} className="relative max-w-xl">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <Input
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setSearchOpen(true);
                }}
                onFocus={() => setSearchOpen(true)}
                placeholder="Filtra las ciudades disponibles…"
                className="pl-9"
                autoComplete="off"
                disabled={loading}
              />
              {searchOpen && (
                <div
                  role="listbox"
                  className="absolute left-0 right-0 top-full z-50 mt-1 max-h-64 overflow-y-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
                >
                  {results.length === 0 ? (
                    <p className="px-2 py-1.5 text-sm text-muted-foreground">
                      {available.length === 0
                        ? 'No hay ciudades disponibles.'
                        : 'No hay más ciudades que coincidan.'}
                    </p>
                  ) : (
                    <ul className="space-y-1">
                      {results.map((city) => (
                        <li key={city.id}>
                          <button
                            type="button"
                            role="option"
                            aria-selected={false}
                            onClick={() => handleAdd(city.displayName)}
                            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground"
                          >
                            <MapPin
                              className="size-4 text-muted-foreground"
                              aria-hidden
                            />
                            <span className="min-w-0 flex-1 truncate font-medium">
                              {city.displayName}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {city.eventCount}
                            </span>
                            <Plus
                              className="size-4 text-muted-foreground"
                              aria-hidden
                            />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </section>

          <section className="space-y-3">
            <header className="flex items-baseline justify-between">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Ciudades en la home
              </h3>
              <span className="text-xs text-muted-foreground">
                {selected.length}{' '}
                {selected.length === 1 ? 'ciudad' : 'ciudades'}
              </span>
            </header>
            {loading ? (
              <p className="rounded-md border bg-card p-4 text-sm text-muted-foreground">
                Cargando…
              </p>
            ) : selected.length === 0 ? (
              <p className="rounded-md border border-dashed bg-card p-4 text-center text-sm text-muted-foreground">
                Aún no has añadido ninguna ciudad.
              </p>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={selected}
                  strategy={verticalListSortingStrategy}
                >
                  <ul className="max-w-xl space-y-2">
                    {selected.map((name) => (
                      <SortableCityRow
                        key={name}
                        name={name}
                        onRemove={() => handleRemove(name)}
                      />
                    ))}
                  </ul>
                </SortableContext>
              </DndContext>
            )}
          </section>

          {saveError && <p className="text-sm text-destructive">{saveError}</p>}
        </>
      )}
    </div>
  );
}

interface SortableCityRowProps {
  name: string;
  onRemove(): void;
}

function SortableCityRow({ name, onRemove }: SortableCityRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: name });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <li ref={setNodeRef} style={style} className={cn(isDragging && 'z-10')}>
      <article
        className={cn(
          'flex items-center gap-3 rounded-lg border bg-card p-2',
          isDragging && 'shadow-lg ring-2 ring-foreground/20',
        )}
      >
        <button
          type="button"
          aria-label="Arrastra para reordenar"
          className="flex size-8 shrink-0 cursor-grab touch-none items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="size-4" aria-hidden />
        </button>
        <MapPin className="size-5 shrink-0 text-muted-foreground" aria-hidden />
        <span className="min-w-0 flex-1 truncate text-sm font-medium">{name}</span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Quitar ciudad"
          onClick={onRemove}
          className="text-muted-foreground hover:text-destructive"
        >
          <X className="size-4" aria-hidden />
        </Button>
      </article>
    </li>
  );
}

function formatApiError(err: ApiClientError): string {
  if (err.code === 'etag_mismatch') {
    return 'Otra persona modificó la lista. Recarga la página y vuelve a intentarlo.';
  }
  return err.message || 'No se pudieron guardar las ciudades destacadas.';
}
